// src/components/AdminPanel/CertPanel.jsx


import React, { useState } from "react";
import styled from "styled-components";
import {
  runCertificate,
  getCertificateViewUrl,
  getCertificateDownloadUrl,
} from "../../services/certificateApi";

import { AdminButton } from "../../pages/admin/action_engine";
const CERT_TYPES = [
  {
    key: "nf525",
    title: "NF525 - B2C",
    subtitle: "Caisse & fiscalité",
    description: "Chaîne d’intégrité, export fiscal, signature, schéma XML.",
  },
  {
    key: "P1_certification_z",
    title: "Z Certification Chain",
    subtitle: "Caisse",
    description:
      "Chaîne de certification des clôtures Z : génération, archivage, export, transmission et contrôles d'intégrité.",
  },
  {
    key: "replenishment",
    title: "NF525 Like - B2B",
    subtitle: "Achats & approvisionnement",
    description:
      "Contrôle du réassort, certification des commandes fournisseurs, paiements et chaîne d’intégrité.",
  },
  {
    key: "cfonb_fec",
    title: "CFONB / FEC / SEPA",
    subtitle: "Banque & comptabilité",
    description: "Exports bancaires, FEC enrichi, SEPA, SAF-T et tâches async.",
  },
  {
    key: "activity_accounting",
    title: "Activity Accounting Chain",
    subtitle: "Multi-activité & comptabilité",
    description:
      "Validation complète de la chaîne Activity → Cart → Receipt → SalesLine → Accounting → FEC → NF525.",
  },
  {
    key: "company_isolation",
    title: "Company Isolation",
    subtitle: "Multi-société",
    description:
      "Certification du cloisonnement des données par société : admin, caisse, tickets, shop, CRM, calendrier, fournisseurs, factures et comptabilité.",
  },
  {
    key: "frontend_contexts",
    title: "Frontend Context Coverage",
    subtitle: "React & multi-company",
    description:
      "Certification des Contexts React : authentification, société courante, scope admin, panier, tickets, boutique, filtres et tâches async.",
  },
  {
    key: "admin_scope",
    title: "Frontend Admin Scope",
    subtitle: "React & périmètre administrateur",
    description:
      "Certification du scope administrateur frontend : propagation selectedCompanyUuid, panels métiers, banque, comptabilité, sociétés, activités POS et certifications.",
  },
  {
    key: "pos_frontend",
    title: "POS Frontend Multi-Company",
    subtitle: "Caisse, paiement & tickets",
    description:
      "Certification de la verticale POS frontend : CartPage, CartContext, pending carts, tickets, ReceiptContext, PayModal, useCheckoutState, paiements, remises, PDF et propagation companyUuid / selectedCompanyUuid.",
  },
];

const CertPanel = () => {
  const [selectedCertType, setSelectedCertType] = useState("nf525");
  const [certRunningType, setCertRunningType] = useState(null);
  const [certReport, setCertReport] = useState("");
  const [certError, setCertError] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [certPdfUrl, setCertPdfUrl] = useState(null);

  const runCertificateTest = async (type) => {
    setSelectedCertType(type);
    setCertRunningType(type);
    setCertError(null);
    setCertReport("");
    setCertFile(null);
    setCertPdfUrl(null);

    try {
      const response = await runCertificate(type);
      const file = response.data?.file || null;

      setCertReport(response.data?.report || "Rapport indisponible.");
      setCertFile(file);

      if (file) {
        setCertPdfUrl(getCertificateViewUrl(file));
      }
    } catch (err) {
      setCertError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          err?.message ||
          "Erreur lors de l'exécution du certificat."
      );
      setCertReport(err?.response?.data?.report || "");
    } finally {
      setCertRunningType(null);
    }
  };

  return (
    <CertificateWorkspace>
      <WorkspaceMain>
        <WorkspaceBody>
          <CertificateGrid>
            {CERT_TYPES.map((cert) => (
              <CertificateCard
                key={cert.key}
                className={selectedCertType === cert.key ? "selected" : ""}
                onClick={() => setSelectedCertType(cert.key)}
              >
                <div>
                  <CertBadge>{cert.subtitle}</CertBadge>
                  <h3>{cert.title}</h3>
                  <p>{cert.description}</p>
                </div>

                <AdminButton variant="primary"
                  disabled={Boolean(certRunningType)}
                  onClick={(event) => {
                    event.stopPropagation();
                    runCertificateTest(cert.key);
                  }}
                >
                  {certRunningType === cert.key
                    ? "Exécution en cours..."
                    : `Exécuter ${cert.title}`}
                </AdminButton>
              </CertificateCard>
            ))}
          </CertificateGrid>

          {certError && <ErrorBox>{certError}</ErrorBox>}
        </WorkspaceBody>
      </WorkspaceMain>

      <SidePanel>
        <ReportContainer>
          {certPdfUrl ? (
            <PdfViewer src={certPdfUrl} title="Certificat PDF" />
          ) : (
            <ReportViewer>
              {certReport ||
                "Aucun rapport chargé. Lance un test pour afficher le résultat ici."}
            </ReportViewer>
          )}

          {certReport && (
            <RawReport>
              <h3>Sortie d’exécution</h3>
              <ReportViewer>{certReport}</ReportViewer>
            </RawReport>
          )}

          {certFile && (
            <DownloadButton
              href={getCertificateDownloadUrl(certFile)}
              target="_blank"
              rel="noreferrer"
            >
              Enregistrer sous / Télécharger
            </DownloadButton>
          )}
        </ReportContainer>
      </SidePanel>
    </CertificateWorkspace>
  );
};

export default CertPanel;

const CertificateWorkspace = styled.main`
  display: flex;
  width: 100%;
  height: calc(100vh - 150px);
  gap: 1.4rem;
  align-items: stretch;
  margin-top: 0.6rem;

  @media (max-width: 1150px) {
    height: auto;
    flex-direction: column;
  }
`;

const WorkspaceMain = styled.section`
  flex: 1 1 auto;
  min-width: 0;
  background: #1d263d;
  border-radius: 28px;
  overflow: hidden;
`;

const WorkspaceBody = styled.div`
  height: calc(100% - 72px);
  padding: 2rem;
  overflow-y: auto;
  box-sizing: border-box;
`;

const CertificateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.1rem;
`;

const CertificateCard = styled.div`
  min-height: 250px;
  background: #202a43;
  border-radius: 18px;
  color: #f7f7f7;
  padding: 1.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: 0.18s ease;

  &.selected,
  &:hover {
    background: #26324f;
    border-color: rgba(255, 255, 255, 0.28);
    transform: translateY(-2px);
  }

  h3 {
    margin: 0.8rem 0 0.5rem;
  }

  p {
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.45;
  }
`;

const CertBadge = styled.span`
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 900;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
`;


const SidePanel = styled.aside`
  width: 34vw;
  min-width: 460px;
  max-width: 720px;
  flex: 0 0 34vw;
  color: #f7f7f7;
  min-height: 0;

  @media (max-width: 1150px) {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: auto;
  }
`;

const ReportContainer = styled.div`
  height: calc(100% - 44px);
  background: #1d263d;
  border-radius: 24px;
  padding: 1.3rem;
  box-sizing: border-box;
  overflow-y: auto;
`;

const PdfViewer = styled.iframe`
  width: 100%;
  height: calc(100vh - 280px);
  min-height: 520px;
  border: none;
  border-radius: 16px;
  background: #fff;
`;

const ReportViewer = styled.pre`
  width: 100%;
  min-height: 480px;
  max-height: calc(100vh - 310px);
  overflow: auto;
  padding: 1rem;
  border-radius: 14px;
  background: #080e1f;
  color: #f7f7f7;
  white-space: pre-wrap;
  font-size: 0.82rem;
  line-height: 1.45;
  box-sizing: border-box;
`;

const RawReport = styled.div`
  margin-top: 1rem;

  h3 {
    margin: 0 0 0.7rem;
    color: #f7f7f7;
  }

  ${ReportViewer} {
    min-height: 180px;
    max-height: 260px;
  }
`;

const ErrorBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 12px;
  color: #fff;
  background: rgba(180, 40, 40, 0.9);
`;

const DownloadButton = styled.a`
  display: block;
  margin-top: 1.1rem;
  text-align: center;
  padding: 1rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 950;
  background: #fff9ea;
  color: #0b1328;
`;
